import { validateVisitFields } from "../../city-visits/cityVisitsModel";
import { validateMediaUpload } from "../../media-gallery/mediaModel";
import { ADVICE_SECTIONS } from "../../../constants/tspAdvice";
import { EMPTY_ADVICE, EMPTY_RESPONSE, adviceWritten, validateAccommodation, validateAdviceSections } from "../addJourneyCityDetails";

// The place store of a journey being CREATED: every section is held in the
// browser, in the same `details` shape Add Journey has always kept per stop
// (addJourneyCityDetails.js emptyCityDetails), and saved right after the server
// creates the journey (saveJourneyCityDetails.js). Book distribution, media and
// public response need no Visit Info: a place with any of them but no visit
// gets its visit recorded automatically when the journey is saved. This only gives the sections their uniform store interface:
//   records (visit, books, stays, media, advice, responses), hasVisit, and
//   add* / update* / remove* actions returning { ok } or { ok: false, message }.
// `onChange(updater)` updates this place's details; `range` = the journey's
// dates as they stand (from the places' dates), for the visit date.
//
// Advice and public response are one per place here (the details shape holds
// one of each); on Edit Journey a place may have several.

let stagedCounter = 0;
const nextId = (prefix) => {
  stagedCounter += 1;
  return `${prefix}-${stagedCounter}`;
};

const ok = { ok: true };
const fail = (message) => Promise.resolve({ ok: false, message });
const done = (change) => {
  change();
  return Promise.resolve(ok);
};

export default function stagedPlaceStore({ details, onChange, range }) {
  const { visit } = details;
  const hasVisit = Boolean(visit.enabled);

  const saveVisit = (draft) => {
    const checked = validateVisitFields(draft, range);
    if (checked.error) return fail(checked.error);
    const { id, ...fields } = draft;
    return done(() => onChange((current) => ({ ...current, visit: { ...current.visit, ...fields, enabled: true } })));
  };

  const update = (key, mapper) => onChange((current) => ({ ...current, [key]: mapper(current[key]) }));

  return {
    staged: true,
    hasVisit,
    visit: hasVisit ? { id: "visit", date: visit.date, time: visit.time, areasVisited: visit.areasVisited, latitude: visit.latitude, longitude: visit.longitude, remarks: visit.remarks } : null,
    saveVisit,
    // Without Visit Info, a place's books / media / response still get a visit
    // (recorded automatically on its date when the journey is created).
    removeVisit: () => done(() => onChange((current) => ({ ...current, visit: { ...current.visit, enabled: false } }))),

    books: details.books,
    addBook: (row) => done(() => update("books", (rows) => [...rows, { ...row, id: nextId("book") }])),
    updateBook: (id, row) => done(() => update("books", (rows) => rows.map((existing) => (existing.id === id ? { ...row, id } : existing)))),
    removeBook: (id) => done(() => update("books", (rows) => rows.filter((existing) => existing.id !== id))),

    stays: details.accommodations,
    addStay: (draft) => {
      const found = validateAccommodation(draft);
      if (Object.keys(found).length) return fail(Object.values(found)[0]);
      return done(() => update("accommodations", (stays) => [...stays, { ...draft, id: nextId("stay") }]));
    },
    updateStay: (id, draft) => {
      const found = validateAccommodation(draft);
      if (Object.keys(found).length) return fail(Object.values(found)[0]);
      return done(() => update("accommodations", (stays) => stays.map((stay) => (stay.id === id ? { ...draft, id } : stay))));
    },
    removeStay: (id) => done(() => update("accommodations", (stays) => stays.filter((stay) => stay.id !== id))),

    media: details.media.map((item) => ({ id: item.id, caption: item.caption, fileName: item.file?.name, size: item.file?.size })),
    addMedia: ({ file, caption }) => {
      const found = validateMediaUpload({ caption, journeyId: "x", cityVisitId: "x" }, file);
      if (found.file || found.caption) return fail(found.file || found.caption);
      return done(() => update("media", (items) => [...items, { id: nextId("media"), file, caption: caption.trim() }]));
    },
    removeMedia: (id) => done(() => update("media", (items) => items.filter((item) => item.id !== id))),

    advice: adviceWritten(details.advice) ? [{ id: "advice", sections: details.advice }] : [],
    adviceMax: 1,
    addAdvice: (sections) => saveAdvice(sections, onChange),
    updateAdvice: (id, sections) => saveAdvice(sections, onChange),
    removeAdvice: () => done(() => onChange((current) => ({ ...current, advice: { ...EMPTY_ADVICE } }))),

    responses: details.response.rating ? [{ id: "response", rating: details.response.rating, remarks: details.response.remarks }] : [],
    responsesMax: 1,
    addResponse: (response) => done(() => onChange((current) => ({ ...current, response: { rating: response.rating, remarks: response.remarks || "" } }))),
    updateResponse: (id, response) => done(() => onChange((current) => ({ ...current, response: { rating: response.rating, remarks: response.remarks || "" } }))),
    removeResponse: () => done(() => onChange((current) => ({ ...current, response: { ...EMPTY_RESPONSE } }))),
  };
}

function saveAdvice(sections, onChange) {
  const tidy = Object.fromEntries(ADVICE_SECTIONS.map((section) => [section.key, sections[section.key] || ""]));
  if (!adviceWritten(tidy)) return fail("Write at least one section of advice.");
  const problem = validateAdviceSections(tidy);
  if (problem) return fail(problem);
  return done(() => onChange((current) => ({ ...current, advice: tidy })));
}
